import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Sparkles, Copy, Check, Mail, Phone, MessageSquare, AlertTriangle, Target, Zap, FileCheck } from "lucide-react";

function toText(v) {
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (Array.isArray(v)) return v.map(toText).join("\n");
  if (typeof v === "object") {
    if ("subject" in v || "body" in v) return `Subject: ${v.subject || ""}\n\n${v.body || ""}`.trim();
    return Object.entries(v).map(([k, val]) => `${k}: ${toText(val)}`).join("\n");
  }
  return String(v);
}

function CopyBlock({ title, icon: Icon, content, testid }) {
  const text = toText(content);
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="rounded-md border border-border bg-white" data-testid={testid}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-slate-50">
        <div className="flex items-center gap-2 text-xs font-semibold text-navy uppercase tracking-wider">
          {Icon && <Icon className="h-3.5 w-3.5 text-gold-dark" aria-hidden="true" />} {title}
        </div>
        <button onClick={copy} className="text-muted-foreground hover:text-navy transition-colors" data-testid={`${testid}-copy`}>
          {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
        </button>
      </div>
      <pre className="px-3 py-2.5 text-sm whitespace-pre-wrap font-sans leading-relaxed text-slate-700">{text}</pre>
    </div>
  );
}

const DECISION_STYLES = {
  Yes: "bg-emerald-100 text-emerald-800 border-emerald-200",
  No: "bg-red-100 text-red-800 border-red-200",
  "Needs discovery": "bg-amber-100 text-amber-800 border-amber-200",
};

export function SalesAssistant({ leadId, assistant, onGenerated }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(assistant);

  const generate = async () => {
    setLoading(true);
    try {
      const res = await api.post(`/leads/${leadId}/assistant`);
      setData(res.data);
      onGenerated?.(res.data);
      toast.success("Sales guidance drafted");
    } catch {
      toast.error("Assistant failed to generate");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-md border border-border bg-slate-50 border-l-4 border-l-gold" data-testid="sales-assistant-panel">
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-gold-dark" aria-hidden="true" />
          <div>
            <h3 className="font-display font-bold text-navy leading-none">Sales Assistant</h3>
            <p className="text-[11px] text-muted-foreground mt-1">Drafts for Steve to review — nothing is sent automatically.</p>
          </div>
        </div>
        <Button data-testid="generate-assistant-button" onClick={generate} disabled={loading}
          className="bg-navy text-white hover:bg-navy-700">
          <Sparkles className="h-4 w-4 mr-2" />
          {loading ? "Drafting…" : data ? "Regenerate" : "Generate Guidance"}
        </Button>
      </div>

      <div className="p-5 space-y-3">
        {!data ? (
          <div className="text-center py-10 text-muted-foreground text-sm">
            Click <span className="font-medium text-navy">Generate Guidance</span> to draft a summary, scripts, and a follow-up email for this lead.
          </div>
        ) : (
          <>
            <div className="grid md:grid-cols-2 gap-3">
              <CopyBlock title="Lead Summary" icon={FileCheck} content={data.lead_summary} testid="assistant-summary" />
              <CopyBlock title="Buying Trigger" icon={Zap} content={data.buying_trigger} testid="assistant-trigger" />
              <CopyBlock title="Main Pain" icon={Target} content={data.main_pain} testid="assistant-pain" />
              <CopyBlock title="Recommended Next Action" icon={Zap} content={data.recommended_next_action} testid="assistant-next-action" />
            </div>

            {Array.isArray(data.red_flags) && data.red_flags.length > 0 && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3" data-testid="assistant-red-flags">
                <div className="flex items-center gap-2 text-xs font-semibold text-red-800 uppercase tracking-wider mb-2">
                  <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" /> Red Flags
                </div>
                <ul className="list-disc pl-5 text-sm text-red-700 space-y-1">
                  {data.red_flags.map((f, i) => <li key={i}>{toText(f)}</li>)}
                </ul>
              </div>
            )}

            <CopyBlock title="Follow-up Email" icon={Mail} content={data.follow_up_email} testid="assistant-email" />
            <div className="grid md:grid-cols-2 gap-3">
              <CopyBlock title="Phone Script" icon={Phone} content={data.phone_script} testid="assistant-phone" />
              <CopyBlock title="Text Message" icon={MessageSquare} content={data.text_message} testid="assistant-text" />
            </div>

            <div className="flex items-center gap-3 pt-1" data-testid="assistant-proposal-decision">
              <span className="text-xs font-semibold text-navy uppercase tracking-wider">Proposal Decision:</span>
              <span className={`inline-block px-3 py-1 rounded text-sm font-semibold border ${DECISION_STYLES[data.proposal_decision] || "bg-slate-100"}`}>
                {data.proposal_decision}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
